import React, { useMemo } from "react";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import {
  daysUntilDate,
  getExpirationDaysForDocType,
  isHardDurationDocType,
} from "../../../../constants/documents/docTypeExpirationDurations";
import Flx from "../../../../components/layout/Flx";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { Card } from "@mui/material";
import SubsectionHeader from "../../../../components/ui/SubsectionHeader";
import size from "lodash/size";
import {
  AlarmOutlined,
  CancelRounded,
  CheckOutlined,
  WarningAmberRounded,
} from "@mui/icons-material";
import Txt from "../../../../components/typography/Txt";
import { green, grey, orange, red } from "@mui/material/colors";
import OverviewItem from "../../../../components/data/OverviewItem";
import { useSelector } from "react-redux";
import { getValidityReferenceMs } from "../../../../utils/documents/validityReference";
import { useIsAcquisitions } from "../../../../pipelines/PipelineContext";

/**
 * Validity configuration.
 * Your original matchedDocTypesWithValidity list only had comments for days.
 * Here we make a best-effort machine-readable map.
 *
 * If you want to change days later, just edit DOC_VALIDITY_DAYS.
 * Use value "YEAR_END" for docs that expire at year end.
 */
const DOC_VALIDITY_DAYS = {
  // Appraisal
  "Secondary Valuation - CDA/ARR": 120,
  "Secondary Valuation - Internal": 120,

  // Borrower Documents
  "Background Report": 90,
  "Credit Report": 90,
  "Loan Application": 180,
  "Borrower Experience - Property Reports": 90,

  // Closing Package
  "Closing Protection Letter": 90,
  "Marked Up Commitment": 90,

  // Liquidity
  "Ocrolus Verification": 45,

  // Property Documents
  "Flood Certificate": "YEAR_END",

  // Terms & Sizer
  "Inspection Report & supporting Draws and Wires": 180,
  "Preliminary HUD": 90,
  "Settlement Statement": 90,

  // Third Party Reports
  "Project Feasibility Analysis": 180,
  "Environmental Report - Phase 1": 180,
  "Environmental Report - Screen Assessment (ESA)": 180,

  // Title Docs
  "Title - CPL": 90,
  "Title - Wire Instructions": 90,
  "Funding Shield Verification - Wire Instructions": 90,
};

/**
 * Original groups/types list (kept for matching by group name).
 * If reportGuidelineExpirationDays missing and docType doesn't match a key above,
 * we'll try matching by group name and fall back to a group default if provided.
 *
 * Right now group defaults are not specified in your data, so group match is
 * only used to allow docType matching within that group list.
 */
export const matchedDocTypesWithValidity = {
  Appraisal: [
    "Secondary Valuation - CDA/ARR",
    "Secondary Valuation - Internal",
  ],
  "Borrower Documents": [
    "Background Report",
    "Credit Report",
    "Loan Application",
    "Borrower Experience - Property Reports",
  ],
  "Closing Package": ["Closing Protection Letter", "Marked Up Commitment"],
  Liquidity: ["Ocrolus Verification"],
  "Property Documents": ["Flood Certificate"],
  "Terms & Sizer": [
    "Inspection Report & supporting Draws and Wires",
    "Preliminary HUD",
    "Settlement Statement",
  ],
  "Third Party Reports": [
    "Project Feasibility Analysis",
    "Environmental Report - Phase 1",
    "Environmental Report - Screen Assessment (ESA)",
  ],
  "Title Docs": [
    "Title - CPL",
    "Title - Wire Instructions",
    "Funding Shield Verification - Wire Instructions",
  ],
};

export const EXPIRING_SOON_THRESHOLD_DAYS = 10;

/**
 * Compute the validity status (valid / expiringSoon / expired) for a
 * single doc. Exposed so callers outside this renderer (the Documents
 * tab counts widget + the new DocumentValidityList section) can reuse
 * the same business rules without duplicating them.
 *
 * Returns null when the doc has no `reportEffectiveDate` or is hidden —
 * those docs aren't part of the time-sensitive set at all.
 */
export const computeDocValidity = (doc, nowMs = Date.now()) => {
  if (!doc?.reportEffectiveDate) return null;
  if (doc?.isHidden) return null;
  const effective = new Date(doc.reportEffectiveDate);
  if (Number.isNaN(effective.getTime())) return null;

  const expirationDays = resolveValidityDays(doc);
  // No window at all → the doc simply doesn't expire, so it isn't part of the
  // valid / expiringSoon / expired set. Returning null here is what keeps this
  // in step with the Documents table's Validity column, which renders
  // "Doesn't expire" for the same docs.
  //
  // This used to fall through: `computeExpirationDate(effective, null)`
  // returns null, `expirationMs` then defaulted to the EFFECTIVE date, and
  // `remainingSpan` went negative the day after the doc took effect — so every
  // dated doc without a known window reported "expired" forever, and the
  // Expired filter listed rows the table said don't expire.
  if (expirationDays == null) return null;
  const expiration = computeExpirationDate(effective, expirationDays);
  const expirationMs = expiration?.getTime?.() ?? effective.getTime();
  const totalSpan = Math.max(1, expirationMs - effective.getTime());
  const remainingSpan = expirationMs - nowMs;
  const percentRemaining = Math.max(
    0,
    Math.min(100, (remainingSpan / totalSpan) * 100),
  );
  // Whole calendar days remaining, measured the timezone-stable way (local
  // calendar midnight to midnight, matching DocExpirationCell) rather than
  // dividing a raw instant span — the latter drifts a full day near timezone
  // boundaries and disagreed with the table's countdown. YEAR_END keeps the
  // legacy instant math (its expiration is a local end-of-day, not a date).
  const remainingDays =
    expiration != null && expirationDays !== "YEAR_END"
      ? daysUntilDate(expiration, nowMs)
      : Math.ceil(remainingSpan / (1000 * 60 * 60 * 24));

  let status;
  if (remainingDays < 0) status = "expired";
  else if (remainingDays <= EXPIRING_SOON_THRESHOLD_DAYS)
    status = "expiringSoon";
  else status = "valid";

  return {
    effective,
    expirationDays,
    expiration,
    expirationMs,
    remainingDays,
    percentRemaining,
    status,
  };
};


const MAX_RESULTS_GRID_WIDTH = "420px";

const LoanExpiringDocumentsRenderer = ({ documents = [] }) => {
  // Same reference day as the Documents tab: today while the loan is live,
  // frozen at closing once it's closed. Without this the two tabs contradict
  // each other on the same closed loan — every dated doc reads "expired" here
  // months after funding even though it was valid at close.
  // See utils/documents/validityReference.
  const loan = useSelector((state) => state.loanDrilldown);
  const isAcquisition = useIsAcquisitions();
  const validityNowMs = useMemo(
    () => getValidityReferenceMs(loan, { isAcquisition }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAcquisition,
      loan?.loanStatus,
      loan?.loanClosed,
      loan?.originationDate,
      loan?.fundedDate,
      loan?.estimatedClosingDate,
      loan?.expectedClosingDate,
    ]
  );

  const reportDocs = useMemo(
    () => documents.filter((doc) => doc.reportEffectiveDate && !doc?.isHidden),
    [documents]
  );

  // One rule, one place: this used to re-implement computeDocValidity inline
  // and drifted from it (same "no window reads as expired" bug).
  const docsWithStatus = useMemo(() => {
    const out = [];
    for (const doc of reportDocs) {
      const v = computeDocValidity(doc, validityNowMs);
      if (v) out.push({ ...doc, ...v });
    }
    return out;
  }, [reportDocs, validityNowMs]);

  // Keyed off the docs that actually carry a window, not every dated doc —
  // otherwise a loan whose dated docs all lack a window renders an empty card.
  if (size(docsWithStatus) === 0) {
    return null;
  }
  return (
    <Flx column sx={{ mt: 1, mb: 3, p: 2 }}>
      <SubsectionHeader
        title={`Expiring Documents (${size(reportDocs)})`}
        // title={`Time Sensitive Documents (${size(reportDocs)})`}
        icon={<AlarmOutlined className="thin" fontSize="small" />}
      />

      <FlxGrid gap={2} width={MAX_RESULTS_GRID_WIDTH} grow={0}>
        {docsWithStatus.map((doc) => (
          <DocumentCard
            key={doc._id || doc.file_id || doc.loanNumber}
            doc={doc}
          />
        ))}
      </FlxGrid>
    </Flx>
  );
};

/** -------- helpers -------- */

const normalize = (v) =>
  String(v || "")
    .toLowerCase()
    .trim();

const findDocTypeValidityDays = (docType) => {
  if (!docType) return null;
  if (DOC_VALIDITY_DAYS[docType] != null) return DOC_VALIDITY_DAYS[docType];

  // try fuzzy "contains" match against known keys
  const docTypeNorm = normalize(docType);
  const matchKey = Object.keys(DOC_VALIDITY_DAYS).find(
    (k) =>
      normalize(k).includes(docTypeNorm) || docTypeNorm.includes(normalize(k))
  );
  if (matchKey) return DOC_VALIDITY_DAYS[matchKey];

  return null;
};

const findGroupValidityDays = (docGroup) => {
  if (!docGroup) return null;
  const groupNorm = normalize(docGroup);

  // If group name matches one of the keys, we still need a doc type to select days.
  // Since your source doesn't include group-level default days, return null here.
  const groupKey = Object.keys(matchedDocTypesWithValidity).find(
    (k) => normalize(k) === groupNorm
  );
  if (!groupKey) return null;

  return null;
};

/**
 * The validity window for a doc, in days ("YEAR_END" for the year-end types),
 * or null when the doc carries no window and therefore never expires.
 *
 * Order matters. An explicit `reportGuidelineExpirationDays` on the doc always
 * wins; then the CANONICAL doc-type map (constants/documents/
 * docTypeExpirationDurations) — the one the upload queue auto-fills from and
 * the Documents table's Validity column reads, so the two surfaces can't
 * disagree; then this file's older `DOC_VALIDITY_DAYS`, which still carries a
 * handful of types the canonical map hasn't picked up (Loan Application,
 * Preliminary HUD, Settlement Statement, the environmental reports…).
 */
export const resolveValidityDays = (doc) => {
  // Hard doc types always age by the current rule, not the number stamped at
  // upload — see resolveDocExpirationDays.
  if (isHardDurationDocType(doc?.docType))
    return getExpirationDaysForDocType(doc.docType);
  const explicit = Number(doc?.reportGuidelineExpirationDays);
  if (!Number.isNaN(explicit) && explicit > 0) return explicit;
  const canonical = getExpirationDaysForDocType(doc?.docType);
  if (canonical != null) return canonical;
  return getExpirationDays(doc);
};

export const getExpirationDays = (doc) => {
  const explicitDays = Number(doc.reportGuidelineExpirationDays);
  if (!Number.isNaN(explicitDays) && explicitDays > 0) return explicitDays;

  const byDocType =
    findDocTypeValidityDays(doc.docType) ||
    findDocTypeValidityDays(doc.file_display_name);
  if (byDocType != null) return byDocType;

  const byGroup =
    findGroupValidityDays(doc.docGroup) ||
    findGroupValidityDays(doc.group) ||
    findGroupValidityDays(doc.docTypeGroup);
  if (byGroup != null) return byGroup;

  // no match => unknown validity
  return null;
};

export const computeExpirationDate = (effectiveDate, expirationDays) => {
  if (!effectiveDate || Number.isNaN(effectiveDate.getTime())) return null;

  if (expirationDays === "YEAR_END") {
    const y = effectiveDate.getFullYear();
    const yearEnd = new Date(y, 11, 31, 23, 59, 59, 999);
    return yearEnd;
  }

  const daysNum = Number(expirationDays);
  if (Number.isNaN(daysNum) || daysNum <= 0) return null;

  return new Date(effectiveDate.getTime() + daysNum * 24 * 60 * 60 * 1000);
};

/** -------- UI -------- */

const DocumentCard = ({ doc }) => {
  const { chipColor, chipLabel, chipIcon, chipBackgroundColor } = getChipStyles(
    doc.status,
    doc.remainingDays
  );

  const expDaysLabel =
    doc.expirationDays == null
      ? "Unknown"
      : doc.expirationDays === "YEAR_END"
        ? "Year End"
        : `${doc.expirationDays} days`;

  const expirationDateLabel = formatDate(doc.expiration, "Unknown", "M/d/yyyy");
  return (
    <Card
      key={doc._id}
      sx={{
        flexBasis: "340px",
        flexGrow: 1,
        flexShrink: 1,
        p: 1.5,
      }}
    >
      <Flx column gap={1.2}>
        {/* header */}
        <Flx
          fw
          jb
          ac
          sx={{
            gap: 0.8,
            // display: "grid",
            // gridTemplateColumns: "40px 1fr",
            // alignItems: "center",
          }}
        >
          <Flx column gap={0.2}>
            <Txt
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "12px",
                color: grey[900],
              }}
            >
              {doc?.docType}
            </Txt>
            <Txt sx={{ fontSize: "10px" }} secondary fontStyle="italic">
              {doc?.file_display_name}
            </Txt>
          </Flx>
          <Flx
            column
            center
            sx={{
              height: "36px",
              px: 1,
              // width: "36px",
              // background: green[100],
              background: chipBackgroundColor,
              borderRadius: 1,
            }}
          >
            {chipIcon}
            <Txt sx={{ fontSize: "9px", color: chipColor }}>{chipLabel}</Txt>
          </Flx>
        </Flx>

        {/* dates + status */}
        <Flx
          gap={3}
          sx={{
            mt: 1,
            justifyContent: "space-between",
            ".txt": { fontSize: "11px" },
          }}
        >
          <OverviewItem
            label={`Effective Date:`}
            value={formatDate(doc.effective, "Unknown", "M/d/yyyy")}
          />
          <OverviewItem
            label={`Expiration Date:`}
            value={expirationDateLabel}
          />
          <OverviewItem label={`Valid For:`} value={expDaysLabel} />
          <OverviewItem
            label={`Days Remaining:`}
            value={
              doc.remainingDays == null
                ? "Unknown"
                : `${doc.remainingDays} day(s)`
            }
          />
        </Flx>
        {/* <Flx jb ac>
          <Flx gap={3} sx={{ ".txt": { fontSize: "11px" } }}>
            <OverviewItem
              label={`Start:`}
              value={
                doc.effective ? doc.effective.toLocaleDateString() : "Unknown"
              }
            />
            <OverviewItem label={`Valid For:`} value={expDaysLabel} />
            <OverviewItem
              label={`Remaining:`}
              value={
                doc.remainingDays == null
                  ? "Unknown"
                  : `${doc.remainingDays} day(s)`
              }
            />
          </Flx>

          <Flx ac gap={0.6}>
      
            <Flx
              ac
              sx={{
                background: chipBackgroundColor,
                py: 0.35,
                pr: 0.8,
                pl: 0.6,
                borderRadius: 1,
              }}
            >
              {chipIcon}
              <Txt
                sx={{
                  textWrap: "nowrap",
                  color: chipColor,
                  lineHeight: 1.2,
                  fontSize: "11px",
                  fontWeight: "bold",
                }}
              >
                {chipLabel}
              </Txt>
            </Flx>

          
            {doc.remainingDays != null && doc.remainingDays > 0 && (
              <Flx column sx={{ position: "relative" }}>
                <CircularProgress
                  variant="determinate"
                  value={doc.percentRemaining}
                  thickness={5}
                  sx={{
                    width: "46px !important",
                    height: "46px !important",
                    color: chipColor,
                    backgroundColor: "#f5f5f5",
                    borderRadius: "50%",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "23px",
                    left: "23px",
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    background: "#ffffff",
                    justifyContent: "center",
                    borderRadius: "50%",
                    flexDirection: "column",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <Txt
                    sx={{
                      fontSize: "9px",
                      fontWeight: 600,
                      color: chipColor,
                      lineHeight: 1,
                    }}
                  >
                    {doc.remainingDays}
                  </Txt>
                  <Txt
                    sx={{
                      fontSize: "8px",
                      lineHeight: 1,
                      color: chipColor,
                    }}
                  >
                    Days
                  </Txt>
                </Box>
              </Flx>
            )}
          </Flx>
        </Flx> */}
      </Flx>
    </Card>
  );
};

const getChipStyles = (status, remainingDays) => {
  let chipColor, chipLabel, chipIcon, chipBackgroundColor;

  switch (status) {
    case "valid":
      chipColor = green[700];
      chipBackgroundColor = green[50];
      chipLabel = "Valid";
      chipIcon = <CheckOutlined sx={{ color: green[700], fontSize: "15px" }} />;
      break;

    case "expiringSoon":
      chipColor = orange[700];
      chipBackgroundColor = orange[50];
      chipLabel = remainingDays === 0 ? "Expires Today" : "Expiring";
      chipIcon = (
        <WarningAmberRounded sx={{ color: orange[700], fontSize: "15px" }} />
      );
      break;

    case "expired":
      chipColor = red[700];
      chipBackgroundColor = red[50];
      chipLabel = "Expired";
      chipIcon = <CancelRounded sx={{ color: red[700], fontSize: "15px" }} />;
      break;

    default:
      chipColor = grey[600];
      chipBackgroundColor = grey[100];
      chipLabel = "Unknown";
      chipIcon = (
        <WarningAmberRounded sx={{ color: grey[600], fontSize: "15px" }} />
      );
      break;
  }

  return { chipColor, chipLabel, chipIcon, chipBackgroundColor };
};

export default LoanExpiringDocumentsRenderer;
